/**
 * <norse-bundles-buy-box> — bundle proposal selector + quantity multiplier.
 * Intercepts the main product form submit when present and adds every bundle line via /cart/add.js.
 */

if (!customElements.get('norse-bundles-buy-box')) {
  function nbbbCartAddJsUrl() {
    const u = window.routes?.cart_add_url || '/cart/add';
    return /\.js(\?|$)/.test(String(u)) ? u : `${String(u).replace(/\/?$/, '')}.js`;
  }

  function nbbbFormatMoney(cents) {
    const rounded = Math.round(Number(cents) / 100) * 100;
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(rounded).replace(/\.\d{2}/, '');
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rounded / 100);
  }

  function nbbbPercentOff(compareCents, saleCents) {
    const c = Number(compareCents) || 0;
    const s = Number(saleCents) || 0;
    if (c <= s || c <= 0) return 0;
    return Math.round(((c - s) / c) * 100);
  }

  customElements.define(
    'norse-bundles-buy-box',
    class NorseBundlesBuyBox extends HTMLElement {
      connectedCallback() {
        this._onDocSubmit = this._onDocSubmit.bind(this);
        this._onSubmitButtonClick = this._onSubmitButtonClick.bind(this);
        this._isSubmittingBundle = false;
        document.addEventListener('submit', this._onDocSubmit, true);
        this._submitBtn = this._resolveSubmitButton();
        if (this._submitBtn) {
          this._submitBtn.addEventListener('click', this._onSubmitButtonClick, true);
        }
        this._mediaWrapper = this.closest('.product')?.querySelector('.product__media-wrapper') || null;
        this._ssrGalleryWrapper  = this._mediaWrapper?.querySelector('[data-nbbb-ssr-gallery]') || null;
        this._nativeHiddenWrapper = this._ssrGalleryWrapper
          ? this._mediaWrapper?.querySelector('.nbbb__gallery-hidden') || null
          : null;
        this._originalCustomGallery = (this._nativeHiddenWrapper || this._mediaWrapper)
          ?.querySelector('.custom-gallery') || null;
        this._mediaGallery = this._nativeHiddenWrapper
          ? this._nativeHiddenWrapper.querySelector('[id^="MediaGallery-"]') || null
          : (document.getElementById(`MediaGallery-${this.sectionId}`) || null);
        this._dynamicCustomGallery = null;

        this._descEl = this.closest('[id^="ProductInfo-"]')?.querySelector('.product__description.rte') || null;
        this._originalDesc = this._descEl ? this._descEl.innerHTML : null;
        this._infoEl = this.querySelector('[data-nbbb-info-block]') || null;
        this._bindProposalCards();
        this._bindQtyOptions();
        this._refreshAllPrices();
        this._syncPrimaryVariantHint();
        this._patchSubmitButtonLabel();
        this._syncGalleryForSelectedProposal();
        this._applySavePercentToBadges();
        this._syncDescriptionForSelectedProposal();
        this._syncInfoForSelectedProposal();
        this._updateRefillInfo();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this._onDocSubmit, true);
        if (this._submitBtn) {
          this._submitBtn.removeEventListener('click', this._onSubmitButtonClick, true);
        }
      }

      get sectionId() {
        const declared = this.dataset.sectionId || '';
        if (declared && document.getElementById(`product-form-${declared}`)) return declared;
        const infoSection = this.closest('[id^="ProductInfo-"]');
        if (infoSection) {
          const id = infoSection.id.replace('ProductInfo-', '');
          if (id && document.getElementById(`product-form-${id}`)) return id;
        }
        return declared;
      }

      get selectedQtyIndex() {
        const opt = this.querySelector('.nbb__bundle-qty .nbb__delivery-option.is-selected');
        const v = parseInt(opt?.dataset.nbbbQtyIdx || '0', 10);
        return Number.isFinite(v) && v >= 0 ? v : 0;
      }

      get selectedQtyUnitCount() {
        const opt = this.querySelector('.nbb__bundle-qty .nbb__delivery-option.is-selected');
        const v = parseInt(opt?.dataset.nbbbUnitCount || '1', 10);
        return Number.isFinite(v) && v >= 1 ? v : 1;
      }

      _itemsRawForQtyIdx(card, idx) {
        if (idx === 1) return card.dataset.nbbbItemsQ1 || card.dataset.nbbbItems || '';
        if (idx === 2) return card.dataset.nbbbItemsQ2 || card.dataset.nbbbItems || '';
        return card.dataset.nbbbItems || '';
      }

      _subCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbSubQ1 ?? card.dataset.nbbbSubQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbSubQ2 ?? card.dataset.nbbbSubQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbSubQ0 ?? '0', 10);
      }

      _cmpCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbCmpQ1 ?? card.dataset.nbbbCmpQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbCmpQ2 ?? card.dataset.nbbbCmpQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbCmpQ0 ?? '0', 10);
      }

      _recurringCentsForQtyIdx(card, idx) {
        if (idx === 1) return parseInt(card.dataset.nbbbRecurringQ1 ?? card.dataset.nbbbRecurringQ0 ?? '0', 10);
        if (idx === 2) return parseInt(card.dataset.nbbbRecurringQ2 ?? card.dataset.nbbbRecurringQ0 ?? '0', 10);
        return parseInt(card.dataset.nbbbRecurringQ0 ?? '0', 10);
      }

      _deliveryLabelForQtyIdx(card, idx) {
        const raw = (idx === 1 ? card.dataset.nbbbDlabelQ1 :
                     idx === 2 ? card.dataset.nbbbDlabelQ2 :
                                 card.dataset.nbbbDlabelQ0) || card.dataset.nbbbDlabelQ0 || '';
        return raw.replace(/\b\w/g, c => c.toUpperCase());
      }

      _ensureRefillInfoEl() {
        if (this._refillInfoEl) return this._refillInfoEl;
        const btn = this._resolveSubmitButton();
        if (!btn) return null;
        const buttonsDiv = btn.closest('.product-form__buttons');
        if (!buttonsDiv) return null;
        const el = document.createElement('div');
        el.className = 'nbbb__refill-info';
        buttonsDiv.after(el);
        this._refillInfoEl = el;
        return el;
      }

      _updateRefillInfo() {
        const el = this._ensureRefillInfoEl();
        if (!el) return;
        const card = this._getSelectedProposalCard();
        if (!card) { el.hidden = true; return; }

        const idx = this.selectedQtyIndex;
        const units = this.selectedQtyUnitCount;
        const recurringTotal = this._recurringCentsForQtyIdx(card, idx);
        if (!recurringTotal) { el.hidden = true; return; }

        const label = this._deliveryLabelForQtyIdx(card, idx);
        const recurringPerUnit = units > 1 ? Math.round(recurringTotal / units) : 0;

        let html = `<span class="nbbb__refill-price">${nbbbFormatMoney(recurringTotal)}</span>`;
        html += `<span class="nbbb__refill-text"> Refill Ships Every ${label}</span>`;
        if (recurringPerUnit > 0) {
          html += `<span class="nbbb__refill-ea"> (${nbbbFormatMoney(recurringPerUnit)}/ea)</span>`;
        }
        el.innerHTML = html;
        el.hidden = false;
      }

      _resolveProductForm() {
        const bySection = document.getElementById(`product-form-${this.sectionId}`);
        if (bySection) return bySection;
        return document.querySelector('form[data-type="add-to-cart-form"]');
      }

      _resolveSubmitButton() {
        const bySection = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (bySection) return bySection;
        const form = this._resolveProductForm();
        return form?.querySelector('button[type="submit"][name="add"]') || null;
      }

      _bindProposalCards() {
        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(card => {
          card.addEventListener('click', () => this._selectProposal(card));
          card.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._selectProposal(card);
            }
          });
        });
      }

      _bindQtyOptions() {
        this.querySelectorAll('.nbb__bundle-qty .nbb__delivery-option').forEach(opt => {
          opt.addEventListener('click', e => {
            e.stopPropagation();
            this._selectQtyOption(opt);
          });
        });
      }

      _selectProposal(card) {
        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        this._refreshAllPrices();
        this._syncPrimaryVariantHint();
        this._patchSubmitButtonLabel();
        this._syncGalleryForSelectedProposal();
        this._applySavePercentToBadges();
        this._syncDescriptionForSelectedProposal();
        this._syncInfoForSelectedProposal();
        this._updateRefillInfo();
      }

      _selectQtyOption(opt) {
        this.querySelectorAll('.nbb__bundle-qty .nbb__delivery-option').forEach(o => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        const radio = opt.querySelector('[data-nbbb-qty-radio]');
        if (radio) radio.checked = true;
        this._refreshAllPrices();
        this._patchSubmitButtonLabel();
        this._updateRefillInfo();
      }

      _getSelectedProposalCard() {
        return this.querySelector('.nbb__variant-option[data-nbbb-proposal-idx].is-selected');
      }

      _parseItems(card) {
        const raw = this._itemsRawForQtyIdx(card, this.selectedQtyIndex);
        if (!raw) return [];

        if (raw.includes('~') || raw.includes('|')) {
          return raw
            .split('|')
            .map(part => part.trim())
            .filter(Boolean)
            .map(part => {
              const bits = part.split('~');
              const id = Number(bits[0]);
              const quantity = Number(bits[1] || 1);
              let sellingPlanId = '';
              let lineCmpCents = null;
              if (bits.length >= 4) {
                sellingPlanId = (bits[2] || '').trim();
                const c = parseInt(bits[3], 10);
                if (Number.isFinite(c) && c > 0) lineCmpCents = c;
              } else if (bits.length >= 3) {
                sellingPlanId = (bits[2] || '').trim();
              }
              if (!Number.isFinite(id) || id <= 0) return null;
              return {
                id,
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                sellingPlanId,
                lineCmpCents,
              };
            })
            .filter(Boolean);
        }

        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map(it => {
              const id = Number(it?.id);
              const quantity = Number(it?.quantity || 1);
              const sellingPlanId = String(it?.selling_plan || it?.sellingPlanId || '').trim();
              if (!Number.isFinite(id) || id <= 0) return null;
              return {
                id,
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                sellingPlanId,
              };
            })
            .filter(Boolean);
        } catch {
          return [];
        }
      }

      _initCustomGallery(el) {
        if (!el) return;
        const run = () => {
          if (window.CustomGallery && typeof window.CustomGallery.init === 'function') {
            window.CustomGallery.init(el);
            return true;
          }
          return false;
        };
        if (run()) return;
        let n = 0;
        const t = setInterval(() => {
          n += 1;
          if (run() || n > 120) clearInterval(t);
        }, 50);
      }

      /**
       * Update the "Save X%" badge on every gallery in this section's media
       * wrapper (default media-gallery + SSR/dynamic custom galleries).
       */
      _applySavePercentToBadges(percent) {
        const pct = Math.max(0, Math.round(Number(percent ?? this._lastSavePct) || 0));
        const scope = this._mediaWrapper || document;
        scope.querySelectorAll('[data-product-media-discount-badge]').forEach(badge => {
          const el = badge.querySelector('[data-discount-percent]');
          if (el) el.textContent = String(pct);
          badge.classList.toggle('product__media-discount-badge--hidden', pct <= 0);
        });
      }

      _hideNativeGalleries() {
        if (!this._nativeHiddenWrapper) {
          this._mediaGallery?.classList.add('nbbb__gallery-hidden');
          this._originalCustomGallery?.classList.add('nbbb__gallery-hidden');
        }
      }

      _showNativeGalleries() {
        this._ssrGalleryWrapper?.classList.add('nbbb__gallery-hidden');
        if (this._nativeHiddenWrapper) {
          this._nativeHiddenWrapper.classList.remove('nbbb__gallery-hidden');
        } else {
          this._mediaGallery?.classList.remove('nbbb__gallery-hidden');
          this._originalCustomGallery?.classList.remove('nbbb__gallery-hidden');
        }
      }

      _syncGalleryForSelectedProposal() {
        const card = this._getSelectedProposalCard();
        if (!card || !this._mediaWrapper) return;

        const proposalIdx = card.dataset.nbbbProposalIdx;

        if (proposalIdx === '0' && this._ssrGalleryWrapper && !this._dynamicCustomGallery) {
          this._ssrGalleryWrapper.classList.remove('nbbb__gallery-hidden');
          this._hideNativeGalleries();
          return;
        }

        const tpl = this.querySelector(`template[data-nbbb-gallery-template="${proposalIdx}"]`);

        if (!tpl) {
          if (this._dynamicCustomGallery) {
            if (window.CustomGallery && typeof window.CustomGallery.destroy === 'function') {
              window.CustomGallery.destroy(this._dynamicCustomGallery);
            }
            if (this._dynamicCustomGallery.parentNode) {
              this._dynamicCustomGallery.remove();
            }
            this._dynamicCustomGallery = null;
          }
          if (proposalIdx === '0' && this._ssrGalleryWrapper) {
            this._ssrGalleryWrapper.classList.remove('nbbb__gallery-hidden');
            this._hideNativeGalleries();
          } else {
            this._ssrGalleryWrapper?.classList.add('nbbb__gallery-hidden');
            this._showNativeGalleries();
          }
          return;
        }

        const galleryNode = tpl.content.firstElementChild?.cloneNode(true);
        if (!galleryNode) return;
        galleryNode.dataset.nbbbGalleryActive = 'true';

        this._ssrGalleryWrapper?.classList.add('nbbb__gallery-hidden');

        const insertRef = this._nativeHiddenWrapper || this._originalCustomGallery || this._mediaGallery;

        if (this._dynamicCustomGallery) {
          if (window.CustomGallery && typeof window.CustomGallery.destroy === 'function') {
            window.CustomGallery.destroy(this._dynamicCustomGallery);
          }
          if (this._dynamicCustomGallery.parentNode) {
            this._dynamicCustomGallery.replaceWith(galleryNode);
          } else if (insertRef && insertRef.parentNode) {
            insertRef.parentNode.insertBefore(galleryNode, insertRef);
          } else {
            this._mediaWrapper.prepend(galleryNode);
          }
        } else if (insertRef && insertRef.parentNode) {
          insertRef.parentNode.insertBefore(galleryNode, insertRef);
        } else {
          this._mediaWrapper.prepend(galleryNode);
        }

        this._dynamicCustomGallery = galleryNode;
        this._hideNativeGalleries();
        this._initCustomGallery(galleryNode);
      }

      _syncDescriptionForSelectedProposal() {
        if (!this._descEl) return;
        const card = this._getSelectedProposalCard();
        if (!card) return;
        const proposalIdx = card.dataset.nbbbProposalIdx;
        const tpl = this.querySelector(`template[data-nbbb-desc-template="${proposalIdx}"]`);
        if (tpl) {
          this._descEl.innerHTML = tpl.innerHTML;
        } else if (this._originalDesc !== null) {
          this._descEl.innerHTML = this._originalDesc;
        }
      }

      _syncInfoForSelectedProposal() {
        if (!this._infoEl) return;
        const card = this._getSelectedProposalCard();
        if (!card) return;
        const proposalIdx = card.dataset.nbbbProposalIdx;
        const tpl = this.querySelector(`template[data-nbbb-info-template="${proposalIdx}"]`);
        this._infoEl.innerHTML = tpl ? tpl.innerHTML : '';
        this._infoEl.hidden = !tpl || !tpl.innerHTML.trim();
        if (!this._infoEl.hidden) this._highlightFreeWords(this._infoEl);
      }

      _highlightFreeWords(el) {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const nodes = [];
        let n;
        while ((n = walker.nextNode())) nodes.push(n);
        nodes.forEach(node => {
          if (!/FREE/i.test(node.textContent)) return;
          const frag = document.createDocumentFragment();
          node.textContent.split(/(FREE)/i).forEach(part => {
            if (/^FREE$/i.test(part)) {
              const span = document.createElement('span');
              span.className = 'nbbb__info-free';
              span.textContent = part;
              frag.appendChild(span);
            } else {
              frag.appendChild(document.createTextNode(part));
            }
          });
          node.parentNode.replaceChild(frag, node);
        });
      }

      _refreshAllPrices() {
        const card = this._getSelectedProposalCard();
        if (!card) return;

        const idx = this.selectedQtyIndex;
        const units = this.selectedQtyUnitCount;

        this.querySelectorAll('.nbb__variant-option[data-nbbb-proposal-idx]').forEach(c => {
          const cSub = this._subCentsForQtyIdx(c, idx);
          const cCmp = this._cmpCentsForQtyIdx(c, 0);
          const cSubPerUnit = units > 1 ? Math.round(cSub / units) : cSub;
          const cmpPerUnit  = cCmp;

          const priceEl = c.querySelector('[data-nbbb-card-price]');
          if (priceEl) {
            if (units > 1) {
              priceEl.innerHTML = nbbbFormatMoney(cSubPerUnit) + '<span class="nbb__vp-each"> /each</span>';
            } else {
              priceEl.textContent = nbbbFormatMoney(cSubPerUnit);
            }
          }

          const cmpEl = c.querySelector('[data-nbbb-card-compare]');
          if (cmpEl) {
            cmpEl.textContent = nbbbFormatMoney(cmpPerUnit);
            cmpEl.classList.toggle('is-hidden', cmpPerUnit <= cSubPerUnit);
          }

          const saveEl = c.querySelector('[data-nbbb-card-save]');
          if (saveEl) {
            const saveCents = cmpPerUnit - cSubPerUnit;
            if (saveCents > 0) {
              saveEl.textContent = `SAVE ${nbbbFormatMoney(saveCents)}`;
              saveEl.classList.remove('is-hidden');
            } else {
              saveEl.textContent = '';
              saveEl.classList.add('is-hidden');
            }
          }
        });

        const subLine = this._subCentsForQtyIdx(card, idx);
        const cmpLine = this._cmpCentsForQtyIdx(card, idx);

        this._lastSavePct = nbbbPercentOff(cmpLine, subLine);
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setSavePercent === 'function') {
          gallery.setSavePercent(this._lastSavePct);
        }
        this._applySavePercentToBadges(this._lastSavePct);

        const items = this._parseItems(card);
        const primaryVid = items.length ? String(items[0].id) : '';
        const activePriceEl = card.querySelector('[data-nbbb-card-price]');
        const activeCmpEl = card.querySelector('[data-nbbb-card-compare]');

        document.dispatchEvent(
          new CustomEvent('norse-bundles-buy-box:change', {
            detail: {
              variantId: primaryVid,
              displayPrice: activePriceEl?.textContent?.trim() || '',
              comparePrice: cmpLine > subLine ? (activeCmpEl?.textContent?.trim() || '') : '',
              bundleQtyIdx: idx,
            },
          })
        );
      }

      /**
       * Keeps theme listeners happy: set hidden variant id to first bundle line item.
       */
      _syncPrimaryVariantHint() {
        const card = this._getSelectedProposalCard();
        const items = this._parseItems(card);
        const vid = items.length ? String(items[0].id) : '';
        if (!vid) return;

        const form = document.getElementById(`product-form-${this.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput) return;
        if (String(idInput.value) === vid) return;
        idInput.value = vid;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(
          new CustomEvent('variant:change', { detail: { variantId: vid, source: 'norse-bundles-buy-box' } })
        );
      }

      _patchSubmitButtonLabel() {
        const btn = this._resolveSubmitButton();
        if (!btn) return;
        const btnSpan = btn.querySelector('span');
        if (!btnSpan) return;

        const card = this._getSelectedProposalCard();
        if (!card) return;

        const q = this.selectedQtyIndex;
        const line = this._subCentsForQtyIdx(card, q);
        if (line > 0) {
          btnSpan.textContent = `ADD TO CART - ${nbbbFormatMoney(line)}`;
        }
      }

      _onDocSubmit(e) {
        const form = e.target;
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        const targetForm = this._resolveProductForm();
        if (!targetForm || form !== targetForm) return;

        if (this._isSubmittingBundle) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();
        this._submitBundle(form, this._resolveSubmitButton());
      }

      _onSubmitButtonClick(e) {
        const btn = e.currentTarget;
        if (!btn) return;
        if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
        const form = this._resolveProductForm();
        if (!form) return;

        const card = this._getSelectedProposalCard();
        if (!card) return;

        const items = this._parseItems(card);
        if (!items.length) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        this._submitBundle(form, btn);
      }

      _generateBundleId() {
        return '_b' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
      }

      _submitBundle(form, submitBtn) {
        if (this._isSubmittingBundle) return;
        const card = this._getSelectedProposalCard();
        if (!card) return;

        const items = this._parseItems(card);
        if (!items.length) return;

        const bundleId = this._generateBundleId();

        const payloadItems = [];
        const linesByKey = new Map();
        items.forEach(it => {
          const id = Number(it.id);
          if (!Number.isFinite(id) || id <= 0) return;
          const quantity = Number(it.quantity || 1);
          const sellingPlanId = String(it.sellingPlanId || '').trim();
          const key = `${id}::${sellingPlanId}`;
          const cmpUnit = Number(it.lineCmpCents);
          const cmpAdd = Number.isFinite(cmpUnit) && cmpUnit > 0 ? Math.round(cmpUnit * quantity) : 0;
          const current = linesByKey.get(key) || {
            id,
            quantity: 0,
            selling_plan: sellingPlanId,
            bundleCmpSum: 0,
          };
          current.quantity += quantity;
          current.bundleCmpSum += cmpAdd;
          linesByKey.set(key, current);
        });
        linesByKey.forEach(line => {
          const out = {
            id: line.id,
            quantity: line.quantity,
            properties: { _bundle_id: bundleId },
          };
          if (line.bundleCmpSum > 0) {
            out.properties._nbbb_cmp = String(Math.round(line.bundleCmpSum));
          }
          if (line.selling_plan) out.selling_plan = Number(line.selling_plan) || line.selling_plan;
          payloadItems.push(out);
        });
        if (!payloadItems.length) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: payloadItems };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map(s => s.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        this._isSubmittingBundle = true;
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
          const spin = submitBtn.closest('product-form')?.querySelector('.loading__spinner');
          spin?.classList.remove('hidden');
        }

        fetch(nbbbCartAddJsUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then(r => r.json())
          .then(response => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'norse-bundles-buy-box',
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              const pf = form.closest('product-form');
              if (pf && typeof pf.handleErrorMessage === 'function') pf.handleErrorMessage(response.description);
              return;
            }

            if (!cart) {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }

            const startMarker =
              typeof CartPerformance !== 'undefined' && CartPerformance.createStartingMarker
                ? CartPerformance.createStartingMarker('add:wait-for-subscribers')
                : null;

            const afterPublish = () => {
              if (startMarker && typeof CartPerformance !== 'undefined' && CartPerformance.measureFromMarker) {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              }
            };

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'norse-bundles-buy-box',
                productVariantId: String(payloadItems[0]?.id || ''),
                cartData: response,
              }).then(afterPublish);
            } else {
              afterPublish();
            }

            const paint = () => cart.renderContents(response);
            if (typeof CartPerformance !== 'undefined' && typeof CartPerformance.measure === 'function') {
              CartPerformance.measure('add:paint-updated-sections', paint);
            } else {
              paint();
            }
          })
          .catch(err => console.error(err))
          .finally(() => {
            this._isSubmittingBundle = false;
            if (submitBtn) {
              submitBtn.removeAttribute('aria-disabled');
              submitBtn.classList.remove('loading');
              const spin = submitBtn.closest('product-form')?.querySelector('.loading__spinner');
              spin?.classList.add('hidden');
            }
          });
      }

    }
  );
}
